import { parseDocument } from 'htmlparser2';
import {
  hasChildren,
  isTag,
  isText,
  type ChildNode,
  type Document,
  type Element,
  type Node,
} from 'domhandler';

export interface DocumentReferenceFragmentClassification {
  readonly hasElements: boolean;
  readonly isDocref: boolean;
  readonly isHtml: boolean;
}

const DOCREF_TAG_NAMES = new Set([
  'list',
  'item',
  'content',
  'caption',
  'footnote',
  'paragraph',
]);

export const parseDocrefDocument = (value: string): Document =>
  parseDocument(value, {
    xmlMode: true,
    recognizeSelfClosing: true,
    lowerCaseAttributeNames: false,
  });

export const parseHtmlDocument = (value: string): Document =>
  parseDocument(value, {
    xmlMode: false,
    recognizeSelfClosing: true,
    lowerCaseAttributeNames: false,
  });

export const classifyMarkupFragment = (
  value: string,
): DocumentReferenceFragmentClassification => {
  const document = parseHtmlDocument(value);
  let hasElements = false;
  let isDocref = false;

  walkNodes(document.children, (node) => {
    if (!isTag(node)) return;

    hasElements = true;
    if (DOCREF_TAG_NAMES.has(node.name.toLowerCase())) {
      isDocref = true;
    }
  });

  return {
    hasElements,
    isDocref,
    isHtml: hasElements && !isDocref,
  };
};

export const findTags = (
  nodes: ReadonlyArray<ChildNode>,
  matcher: (node: Element) => boolean,
): Element[] => {
  const matches: Element[] = [];

  walkNodes(nodes, (node) => {
    if (isTag(node) && matcher(node)) {
      matches.push(node);
    }
  });

  return matches;
};

export const getTagChildren = (node: Node): Element[] =>
  getChildren(node).filter((child): child is Element => isTag(child));

export const getAttribute = (
  node: Element,
  name: string,
): string | undefined => {
  const exact = node.attribs[name];
  if (typeof exact === 'string' && exact.trim()) return exact;

  const lowered = name.toLowerCase();
  const entry = Object.entries(node.attribs).find(
    ([key, value]) => key.toLowerCase() === lowered && Boolean(value?.trim()),
  );
  return entry?.[1];
};

export const getNodeText = (node: Node | ReadonlyArray<Node>): string => {
  const nodes: ReadonlyArray<Node> = Array.isArray(node) ? node : [node];
  const parts: string[] = [];

  for (const entry of nodes) {
    if (isText(entry)) {
      parts.push(entry.data);
      continue;
    }

    if (isTag(entry) && entry.name.toLowerCase() === 'br') {
      parts.push('\n');
      continue;
    }

    const children = getChildren(entry);
    if (children.length > 0) {
      parts.push(getNodeText(children));
    }
  }

  return parts.join('');
};

const walkNodes = (
  nodes: ReadonlyArray<ChildNode>,
  visitor: (node: ChildNode) => void,
): void => {
  nodes.forEach((node) => {
    visitor(node);
    const children = getChildren(node);
    if (children.length > 0) {
      walkNodes(children, visitor);
    }
  });
};

const getChildren = (node: Node): ChildNode[] => {
  if (hasChildren(node)) return node.children;
  return [];
};
